/**
 * ConfluenceUsedInPopover Component
 *
 * Displays a count of playbooks using a confluence item.
 * Click/tap opens a popover showing playbook names with polarity icons.
 * Works on both desktop and mobile.
 */

import React from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
} from '@enfomentis/ui';
import type { ConfluencePolarity } from '@/types/shared/ConfluencePolarity';

export interface ConfluenceUsedInData {
  playbookName: string;
  polarity: ConfluencePolarity;
}

export interface ConfluenceUsedInPopoverProps {
  /** Array of playbook usage data with polarity for this confluence */
  usedInData: ConfluenceUsedInData[];
}

export const ConfluenceUsedInPopover: React.FC<ConfluenceUsedInPopoverProps> = ({ usedInData }) => {
  const count = usedInData.length;

  // Empty state - no playbooks use this item
  if (count === 0) {
    return (
      <span className="text-sm text-text-tertiary">
        0 playbooks
      </span>
    );
  }

  return (
    <Popover isNonModal placement="bottom">
      <PopoverTrigger>
        <Button
          variant="light"
          size="sm"
          className="text-sm text-text-secondary hover:text-text-primary min-w-0 px-1"
        >
          {count} {count === 1 ? 'playbook' : 'playbooks'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-3">
        <div className="w-72 space-y-1">
          <p className="text-xs font-medium text-text-secondary mb-2">
            Used in:
          </p>
          <ul className="space-y-1 max-h-48 overflow-y-auto min-w-0">
            {usedInData.map((entry, index) => (
              <li
                key={index}
                className="flex items-center gap-2 text-sm text-text-primary w-full min-w-0"
                title={entry.playbookName}
              >
                {entry.polarity === 'positive' ? (
                  <ThumbsUp className="w-3 h-3 text-success shrink-0" />
                ) : (
                  <ThumbsDown className="w-3 h-3 text-warning shrink-0" />
                )}
                <span className="truncate min-w-0">{entry.playbookName}</span>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};
